import { useId, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { IconClose } from "@boligportal/bricks-icons";
import { Button, IconButton } from "@boligportal/bricks-ui";
import { RefreshCw } from "lucide-react";
import { Card } from "~/components/ui/Card";
import { useBuildUpdate } from "./useBuildUpdate";

export function BuildUpdateNotice() {
  const { t } = useTranslation("global");
  const { updateAvailable, dismiss } = useBuildUpdate();
  const titleId = useId();
  const noticeRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const notice = noticeRef.current;
    if (!notice) return;
    const updateOffset = () => {
      document.documentElement.style.setProperty(
        "--app-update-offset",
        `${notice.getBoundingClientRect().height + 16}px`,
      );
    };
    updateOffset();
    const observer = new ResizeObserver(updateOffset);
    observer.observe(notice);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--app-update-offset");
    };
  }, [updateAvailable]);

  return (
    <>
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {updateAvailable &&
          `${t("app_update.title")}. ${t("app_update.description")}`}
      </div>
      {updateAvailable && (
        <div
          ref={noticeRef}
          role="region"
          aria-labelledby={titleId}
          className="fixed right-4 bottom-4 z-50 w-90 max-w-[calc(100vw-2rem)]"
        >
          <Card className="border-gray-40 border bg-white p-4 shadow-lg">
            <div className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className="bg-primary-administration-50 text-primary-administration flex size-9 shrink-0 items-center justify-center rounded-lg"
              >
                <RefreshCw className="size-4" />
              </span>
              <div className="min-w-0 flex-1">
                <h2
                  id={titleId}
                  className="pr-5 text-sm font-semibold"
                >
                  {t("app_update.title")}
                </h2>
                <p className="text-muted mt-1 text-sm">
                  {t("app_update.description")}
                </p>
                <div className="mt-3">
                  <Button
                    color="primary-administration"
                    size="sm"
                    onClick={() => window.location.reload()}
                  >
                    {t("app_update.refresh")}
                  </Button>
                </div>
              </div>
              <IconButton
                size="sm"
                className="absolute top-2 right-2"
                aria-label={t("app_update.dismiss")}
                onClick={dismiss}
              >
                <IconClose aria-hidden="true" />
              </IconButton>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
